import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { usePortal } from '../context/PortalContext';
import { API_BASE_URL } from '../config/env';
import { tableService } from '../services/tableService';
import { TableDetails, TableHandHistoryPayload, TableLiveResults } from '../types';
import { formatCurrency, formatDateTime, getGameTypeLabel } from '../utils/formatters';
import { getAuthToken } from '../services/authStorage';

type SeatStatus = 'ACTIVE' | 'FOLDED' | 'FINISHED' | 'ALL_IN' | 'SITTING_OUT';

interface LiveSeat {
  userId: string;
  userName: string;
  position: number;
  stackCurrent: number;
  stackInitial: number;
  status: SeatStatus;
  isDealer: boolean;
  isSmallBlind: boolean;
  isBigBlind: boolean;
  participatingInHand: boolean;
  waitingForNextHand: boolean;
  isAllIn: boolean;
  avatar?: string | null;
  pendingAddChips?: number;
}

interface SocketOccupiedSeatPayload {
  userId: string;
  userName: string;
  avatar?: string | null;
  seatPosition?: number;
  position?: number;
  stackSize?: number;
  pendingAddChips?: number;
  currentBet?: number;
  isDealer?: boolean;
  isSmallBlind?: boolean;
  isBigBlind?: boolean;
  status?: string;
  waitingForNextHand?: boolean;
}

interface LiveGameSnapshot {
  id: string;
  tableId: string;
  phase: string;
  dealerPosition: number;
  smallBlindPosition: number;
  bigBlindPosition: number;
  currentPlayerPosition: number;
  currentPlayerUserId: string | null;
  potAmount: number;
  communityCards: string[];
  variant: string;
  bettingStructure: string;
  actionTimeLimitSeconds: number;
  actionTurnStartedAt: string | null;
  actionTurnExpiresAt: string | null;
  revealedHands: Record<string, string[]>;
  showdownHighlights: Record<string, string[]>;
}

const TABLE_REFRESH_MS = 7000;
const HISTORY_REFRESH_MS = 20000;
const SOCKET_REFRESH_DEBOUNCE_MS = 180;

const deriveSocketBaseUrl = (): string => {
  const apiBaseUrl = API_BASE_URL.replace(/\/+$/, '');
  return apiBaseUrl.replace(/\/api$/, '');
};

const buildAssetUrl = (value?: string | null): string => {
  const rawValue = (value || '').trim();
  if (!rawValue) {
    return '';
  }

  if (/^https?:\/\//i.test(rawValue) || rawValue.startsWith('data:') || rawValue.startsWith('blob:')) {
    return rawValue;
  }

  return `${deriveSocketBaseUrl()}${rawValue.startsWith('/') ? '' : '/'}${rawValue}`;
};

const LiveAvatar: React.FC<{ src?: string | null; userName?: string | null; className: string }> = ({
  src,
  userName,
  className,
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src && !failed) {
    return <img className={className} src={src} alt="" aria-hidden="true" onError={() => setFailed(true)} />;
  }

  return <span className={`${className}-fallback`}>{getInitials(userName)}</span>;
};

const normalizeCardCode = (card?: string): string => {
  const value = (card || '').trim();
  if (!value) {
    return '';
  }

  return value.length > 2 ? value.slice(0, 2).toUpperCase() : value.toUpperCase();
};

const renderSuitGlyph = (card?: string): React.ReactNode => {
  const suit = (card || '').slice(-1).toUpperCase();
  switch (suit) {
    case 'S':
      return <>&spades;</>;
    case 'H':
      return <>&hearts;</>;
    case 'D':
      return <>&diams;</>;
    case 'C':
      return <>&clubs;</>;
    default:
      return '?';
  }
};

const getCardClassName = (card?: string): string => {
  const suit = (card || '').slice(-1).toUpperCase();
  return suit === 'H' || suit === 'D'
    ? 'portal-live-card portal-live-card--red'
    : 'portal-live-card portal-live-card--dark';
};

const renderCard = (card?: string) => {
  const code = normalizeCardCode(card);
  if (!code) {
    return <span className="portal-live-card portal-live-card--empty">-</span>;
  }

  return (
    <span className={getCardClassName(code)}>
      <strong>{code.slice(0, -1)}</strong>
      <small>{renderSuitGlyph(code)}</small>
    </span>
  );
};

const getSeatPositionClassName = (position: number): string => {
  const mapping: Record<number, string> = {
    0: 'portal-live-seat--top-left',
    1: 'portal-live-seat--top-right',
    2: 'portal-live-seat--left-top',
    3: 'portal-live-seat--left',
    4: 'portal-live-seat--left-bottom',
    5: 'portal-live-seat--right-top',
    6: 'portal-live-seat--right',
    7: 'portal-live-seat--right-bottom',
    8: 'portal-live-seat--bottom',
  };

  return mapping[position] || 'portal-live-seat--top';
};

const getResultToneClassName = (value: number): string => {
  if (value > 0) {
    return 'portal-result-tone--positive';
  }

  if (value < 0) {
    return 'portal-result-tone--negative';
  }

  return 'portal-result-tone--neutral';
};

const getInitials = (value?: string | null): string => {
  const text = (value || '').trim();
  if (!text) {
    return '?';
  }

  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const normalizeSeatPosition = (value: unknown): number | null => {
  const numericValue = Number(value);
  if (!Number.isInteger(numericValue) || numericValue < 0 || numericValue > 8) {
    return null;
  }

  return numericValue;
};

const normalizeLiveSeat = (seat: SocketOccupiedSeatPayload | LiveSeat): LiveSeat | null => {
  const seatPosition = 'position' in seat ? seat.position : seat.seatPosition;
  const position = normalizeSeatPosition(seatPosition);
  const stackSize = 'stackSize' in seat ? seat.stackSize : 'stackCurrent' in seat ? seat.stackCurrent : 0;

  if (position === null) {
    return null;
  }

  return {
    userId: seat.userId,
    userName: seat.userName,
    position,
    stackCurrent: Number(stackSize ?? 0),
    stackInitial: Number(stackSize ?? 0),
    status: (seat.status as SeatStatus) || 'ACTIVE',
    isDealer: Boolean(seat.isDealer),
    isSmallBlind: Boolean(seat.isSmallBlind),
    isBigBlind: Boolean(seat.isBigBlind),
    participatingInHand: !seat.waitingForNextHand,
    waitingForNextHand: Boolean(seat.waitingForNextHand),
    isAllIn: String(seat.status || '').toUpperCase() === 'ALL_IN',
    avatar: buildAssetUrl(seat.avatar),
    pendingAddChips: Number(seat.pendingAddChips || 0),
  };
};

const mergeLiveSeats = (socketSeats: LiveSeat[], resultSeats: LiveSeat[]): LiveSeat[] => {
  const resultsByUserId = new Map(resultSeats.map((seat) => [seat.userId, seat]));
  const mergedSocketSeats = socketSeats.map((seat) => {
    const resultSeat = resultsByUserId.get(seat.userId);
    if (!resultSeat) {
      return seat;
    }

    return {
      ...seat,
      userName: seat.userName || resultSeat.userName,
      stackCurrent: Number.isFinite(seat.stackCurrent) ? seat.stackCurrent : resultSeat.stackCurrent,
      stackInitial: seat.stackInitial || resultSeat.stackInitial,
      avatar: seat.avatar || resultSeat.avatar,
    };
  });
  const socketUserIds = new Set(mergedSocketSeats.map((seat) => seat.userId));

  return [...mergedSocketSeats, ...resultSeats.filter((seat) => !socketUserIds.has(seat.userId))]
    .filter((seat) => seat.position >= 0 && seat.position <= 8)
    .sort((left, right) => left.position - right.position);
};

const TableLiveViewPage: React.FC = () => {
  const { tableId = '' } = useParams();
  const { selectedClub, profile } = usePortal();

  const [table, setTable] = useState<TableDetails | null>(null);
  const [liveResults, setLiveResults] = useState<TableLiveResults | null>(null);
  const [history, setHistory] = useState<TableHandHistoryPayload | null>(null);
  const [game, setGame] = useState<LiveGameSnapshot | null>(null);
  const [seats, setSeats] = useState<LiveSeat[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketStatus, setSocketStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const socketRef = useRef<Socket | null>(null);
  const socketRefreshTimerRef = useRef<number | null>(null);

  const activePlayers = useMemo(() => liveResults?.players || [], [liveResults?.players]);
  const boardCards = game?.communityCards || [];
  const currentPlayerId = game?.currentPlayerUserId || null;
  const spectatorName = profile?.nickname || profile?.nome || profile?.nomeCompleto || 'Espectador';
  const spectatorInitials = useMemo(() => getInitials(spectatorName), [spectatorName]);
  const spectatorAvatar = useMemo(
    () => buildAssetUrl(profile?.avatarUrl || profile?.avatar?.url),
    [profile?.avatar?.url, profile?.avatarUrl]
  );
  const resultSeats = useMemo<LiveSeat[]>(() => {
    return (liveResults?.players || [])
      .filter((player) => player.seat_position !== null && player.seat_position !== undefined)
      .map((player) => ({
        userId: player.user_id,
        userName: player.user_name,
        position: Number(player.seat_position),
        stackCurrent: Number(player.total_stack_snapshot || 0),
        stackInitial: Number(player.total_buy_in || 0),
        status: player.is_active ? 'ACTIVE' as SeatStatus : 'FINISHED' as SeatStatus,
        isDealer: false,
        isSmallBlind: false,
        isBigBlind: false,
        participatingInHand: true,
        waitingForNextHand: false,
        isAllIn: false,
        avatar: buildAssetUrl(player.avatar),
        pendingAddChips: 0,
      }));
  }, [liveResults?.players]);
  const normalizedSocketSeats = useMemo(
    () => seats.map((seat) => normalizeLiveSeat(seat)).filter((seat): seat is LiveSeat => Boolean(seat)),
    [seats]
  );
  const liveSeats = useMemo(
    () => mergeLiveSeats(normalizedSocketSeats, resultSeats),
    [normalizedSocketSeats, resultSeats]
  );
  const seatedMe = useMemo(
    () => liveSeats.find((seat) => seat.userId === profile?.id) || null,
    [liveSeats, profile?.id]
  );

  const loadTableData = useCallback(
    async (options?: { silent?: boolean; includeHistory?: boolean }) => {
      if (!tableId) {
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (options?.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const payloads = await Promise.all([
          tableService.getTable(tableId),
          tableService.getLiveResults(tableId),
          options?.includeHistory === false
            ? Promise.resolve(null)
            : tableService.getHandHistory(tableId, { limit: 6, offset: 0 }),
        ]);

        if (requestIdRef.current !== requestId) {
          return;
        }

        setTable(payloads[0]);
        setLiveResults(payloads[1]);
        if (payloads[2]) {
          setHistory(payloads[2]);
        }
        setLastSyncedAt(new Date().toISOString());
      } catch (nextError: any) {
        if (requestIdRef.current === requestId) {
          setError(nextError.message || 'Erro ao carregar a mesa ao vivo');
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [tableId]
  );

  const requestSocketRefresh = useCallback(
    (includeHistory = false) => {
      if (socketRefreshTimerRef.current) {
        window.clearTimeout(socketRefreshTimerRef.current);
      }

      socketRefreshTimerRef.current = window.setTimeout(() => {
        void loadTableData({ silent: true, includeHistory });
      }, SOCKET_REFRESH_DEBOUNCE_MS);
    },
    [loadTableData]
  );

  const refreshGameState = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || !tableId) {
      return;
    }

    socket.emit('game:get-state', tableId, (response: any) => {
      if (!response?.success) {
        setError(response?.error || 'Nao foi possivel atualizar a mesa ao vivo');
        return;
      }

      const nextSeats = Array.isArray(response.seats?.occupiedSeats)
        ? (response.seats.occupiedSeats as SocketOccupiedSeatPayload[])
            .map((seat) => normalizeLiveSeat(seat))
            .filter((seat): seat is LiveSeat => Boolean(seat))
        : [];
      setGame(response.game || null);
      setSeats(nextSeats);
    });
  }, [tableId]);

  useEffect(() => {
    void loadTableData({ includeHistory: true });
  }, [loadTableData]);

  useEffect(() => {
    if (!tableId) {
      return undefined;
    }

    const token = getAuthToken();
    if (!token) {
      return undefined;
    }

    const socket = io(deriveSocketBaseUrl(), {
      transports: ['websocket'],
      auth: { token },
      withCredentials: true,
    });

    socketRef.current = socket;
    setSocketStatus('connecting');

    socket.on('connect', () => {
      setSocketConnected(true);
      setSocketStatus('connected');
      socket.emit('game:join-table', { gameId: tableId }, (response: any) => {
        if (!response?.success) {
          setError(response?.error || 'Nao foi possivel abrir a mesa ao vivo');
          return;
        }

        if (Array.isArray(response.seats?.occupiedSeats)) {
          setSeats(
            (response.seats.occupiedSeats as SocketOccupiedSeatPayload[])
              .map((seat) => normalizeLiveSeat(seat))
              .filter((seat): seat is LiveSeat => Boolean(seat))
          );
        }

        requestSocketRefresh(true);
        refreshGameState();
      });
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
      setSocketStatus('disconnected');
    });

    const refreshEvents = [
      'game:started',
      'game:board-update',
      'game:action-executed',
      'game:showdown-result',
      'game:hand-result',
      'game:player-left',
      'game:seats-updated',
      'game:chips-added',
      'game:player-seated',
      'game:flow-completed',
      'game:finished',
    ];

    refreshEvents.forEach((eventName) => {
      socket.on(eventName, () => {
        requestSocketRefresh(eventName === 'game:hand-result' || eventName === 'game:finished');
        refreshGameState();
      });
    });

    return () => {
      refreshEvents.forEach((eventName) => socket.off(eventName));
      socket.off('connect');
      socket.off('disconnect');
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
      setSocketStatus('disconnected');
      if (socketRefreshTimerRef.current) {
        window.clearTimeout(socketRefreshTimerRef.current);
      }
    };
  }, [loadTableData, refreshGameState, requestSocketRefresh, tableId]);

  useEffect(() => {
    if (!tableId) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (document.hidden) {
        return;
      }

      requestSocketRefresh(false);
    }, TABLE_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [requestSocketRefresh, tableId]);

  useEffect(() => {
    if (!tableId) {
      return undefined;
    }

    const historyTimer = window.setInterval(() => {
      if (document.hidden) {
        return;
      }

      void loadTableData({ silent: true, includeHistory: true });
    }, HISTORY_REFRESH_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void loadTableData({ silent: true, includeHistory: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(historyTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadTableData, tableId]);

  const boardPhaseLabel = game?.phase ? game.phase.replace(/_/g, ' ') : 'AGUARDANDO';
  const tablePhaseClass = game?.phase ? `portal-live-table-stage__table--${game.phase.toLowerCase()}` : 'portal-live-table-stage__table--waiting';
  const phaseToneClass = game?.phase ? `portal-live-table-stage__phase--${game.phase.toLowerCase()}` : 'portal-live-table-stage__phase--waiting';
  const nextPlayers = liveSeats
    .filter((seat) => seat.status !== 'FINISHED')
    .sort((left, right) => left.position - right.position);

  return (
    <div className="portal-live-table-page">
      <header className="portal-live-table-page__header">
        <div className="portal-live-table-page__title-group">
          <p className="portal-live-table-page__eyebrow">Mesa ao vivo</p>
          <h1>{table?.name || tableId}</h1>
          <div className="portal-live-table-page__meta">
            <span>{selectedClub?.name || 'Club'}</span>
            <span>{getGameTypeLabel(table?.gameType)}</span>
            <span>{formatCurrency(table?.smallBlind || 0)} / {formatCurrency(table?.bigBlind || 0)}</span>
            <span>{socketConnected ? 'WebSocket ativo' : socketStatus === 'connecting' ? 'Conectando...' : 'Modo observacao'}</span>
          </div>
        </div>
        <div className="portal-live-table-page__header-actions">
          <div className="portal-live-table-page__spectator-card">
            <div className="portal-live-table-page__spectator-avatar" aria-hidden="true">
              {profile?.id ? (
                <LiveAvatar className="portal-live-table-page__spectator-avatar-image" src={spectatorAvatar} userName={spectatorName} />
              ) : spectatorInitials}
            </div>
            <div className="portal-live-table-page__spectator-copy">
              <strong>{spectatorName}</strong>
              <span>{seatedMe ? `Sentado na posição ${seatedMe.position + 1}` : 'Espectador da mesa'}</span>
            </div>
          </div>
          <span className="portal-chip">
            {loading ? 'Carregando...' : refreshing ? 'Atualizando...' : lastSyncedAt ? `Ultimo sync ${formatDateTime(lastSyncedAt)}` : 'Sem sync ainda'}
          </span>
          <button type="button" className="portal-secondary-button portal-secondary-button--compact" onClick={() => void loadTableData({ silent: true, includeHistory: true })}>
            Atualizar
          </button>
        </div>
      </header>

      {error ? <div className="portal-banner portal-banner--error portal-live-table-page__banner">{error}</div> : null}

      <main className="portal-live-table-page__layout">
        <section className="portal-live-table-stage">
          <div className="portal-live-table-stage__topline">
            <span>Pote {formatCurrency(game?.potAmount || 0)}</span>
            <span>{boardPhaseLabel}</span>
            <span>Relógio {game?.actionTimeLimitSeconds || table?.actionTimeLimitSeconds || 0}s</span>
          </div>

          <div className={`portal-live-table-stage__table ${tablePhaseClass}`}>
            <div className="portal-live-table-stage__table-shadow" aria-hidden="true" />
            <div className="portal-live-table-stage__center">
              <div className="portal-live-table-stage__pot">
                <span>POT</span>
                <strong>{formatCurrency(game?.potAmount || 0)}</strong>
              </div>
              <div className="portal-live-table-stage__board">
                {boardCards.length ? boardCards.map((card, index) => <React.Fragment key={`${card}-${index}`}>{renderCard(card)}</React.Fragment>) : <span className="portal-muted">Board ainda vazio</span>}
              </div>
              <div className={`portal-live-table-stage__phase ${phaseToneClass}`}>{game?.phase || 'WAITING'}</div>
            </div>

            {Array.from({ length: 9 }).map((_, position) => {
              const seat = nextPlayers.find((player) => player.position === position);
              return (
                <div
                  key={position}
                  className={[
                    'portal-live-seat',
                    getSeatPositionClassName(position),
                    seat ? 'portal-live-seat--occupied' : 'portal-live-seat--empty',
                    seat?.userId === currentPlayerId ? 'portal-live-seat--current' : '',
                    seat?.isDealer ? 'portal-live-seat--dealer' : '',
                    seat?.isSmallBlind ? 'portal-live-seat--small-blind' : '',
                    seat?.isBigBlind ? 'portal-live-seat--big-blind' : '',
                    seat?.status === 'FOLDED' ? 'portal-live-seat--folded' : '',
                    seat?.status === 'SITTING_OUT' ? 'portal-live-seat--sitting-out' : '',
                    seat?.isAllIn ? 'portal-live-seat--all-in' : '',
                    seat?.userId === currentPlayerId ? 'portal-live-seat--active-turn' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {seat ? (
                    <>
                      <div className="portal-live-seat__avatar-wrap" aria-hidden="true">
                        <LiveAvatar className="portal-live-seat__avatar" src={seat.avatar} userName={seat.userName} />
                      </div>
                      <span className="portal-live-seat__name">{seat.userName}</span>
                      <strong className="portal-live-seat__stack">{formatCurrency(seat.stackCurrent || 0)}</strong>
                      <small className="portal-live-seat__status">
                        {seat.isAllIn ? 'ALL IN' : seat.waitingForNextHand ? 'Aguardando' : seat.status}
                      </small>
                    </>
                  ) : (
                    <span className="portal-live-seat__empty">Livre</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="portal-live-table-stage__footer">
            <div>
              <span>Jogadores ativos</span>
              <strong>{activePlayers.length}</strong>
            </div>
            <div>
              <span>Tempo real</span>
              <strong>{socketConnected ? 'Ligado' : 'Aguardando'}</strong>
            </div>
            <div>
              <span>Ultima mao</span>
              <strong>{history?.hands?.[0]?.id?.slice(0, 8) || '—'}</strong>
            </div>
          </div>
        </section>

        <aside className="portal-live-table-sidebar">
          <section className="portal-live-table-sidebar__card">
            <div className="portal-live-table-sidebar__card-header">
              <div>
                <p className="portal-section-card__eyebrow">Jogadores</p>
                <h3>Saldo e resultado</h3>
              </div>
              <span className="portal-chip">{liveResults?.players.length || 0} na visao</span>
            </div>

            <div className="portal-live-results-list">
              {liveResults?.players.length ? (
                liveResults.players.map((player) => (
                  <article key={player.user_id} className={`portal-live-result-item ${player.live_result >= 0 ? 'portal-live-result-item--positive' : 'portal-live-result-item--negative'}`}>
                    <div className="portal-live-result-item__identity">
                      <strong>{player.user_name}</strong>
                      <span>{player.is_active ? 'Ativo' : 'Saiu'}</span>
                    </div>
                    <div className="portal-live-result-item__numbers">
                      <span>
                        <small>Stack</small>
                        <strong>{formatCurrency(player.total_stack_snapshot)}</strong>
                      </span>
                      <span>
                        <small>Lucro / perda</small>
                        <strong className={getResultToneClassName(player.live_result)}>
                          {player.live_result > 0
                            ? `+ ${formatCurrency(player.live_result)}`
                            : player.live_result < 0
                              ? `- ${formatCurrency(Math.abs(player.live_result))}`
                              : formatCurrency(0)}
                        </strong>
                      </span>
                      <span>
                        <small>Buy-in</small>
                        <strong>{formatCurrency(player.total_buy_in)}</strong>
                      </span>
                    </div>
                  </article>
                ))
              ) : (
                <div className="portal-empty-state">
                  <strong>Sem jogadores ao vivo</strong>
                  <span className="portal-muted">A mesa vai se preencher assim que a rodada estiver ativa.</span>
                </div>
              )}
            </div>
          </section>

          <section className="portal-live-table-sidebar__card">
            <div className="portal-live-table-sidebar__card-header">
              <div>
                <p className="portal-section-card__eyebrow">Historico recente</p>
                <h3>Ultimas maos</h3>
              </div>
            </div>

            <div className="portal-live-history-list">
              {history?.hands?.length ? (
                history.hands.map((hand) => (
                  <article key={hand.id} className="portal-live-history-item">
                    <div className="portal-live-history-item__header">
                      <strong>{hand.id.slice(0, 8)}</strong>
                      <span>{formatDateTime(hand.played_at)}</span>
                    </div>
                    <div className="portal-live-history-item__board">
                      {hand.community_cards.length ? hand.community_cards.map((card, index) => <React.Fragment key={`${card}-${index}`}>{renderCard(card)}</React.Fragment>) : <span className="portal-muted">Sem board</span>}
                    </div>
                    <div className="portal-live-history-item__footer">
                      <span>Pote {formatCurrency(hand.pot_amount)}</span>
                      <span>{hand.winners.map((winner) => `${winner.user_name} ${formatCurrency(winner.winnings_amount)}`).join(' • ') || 'Sem vencedor informado'}</span>
                    </div>
                  </article>
                ))
              ) : (
                <div className="portal-empty-state">
                  <strong>Nenhuma mao recente</strong>
                  <span className="portal-muted">O historico aparece aqui quando a mesa fechar as maos.</span>
                </div>
              )}
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
};

export default TableLiveViewPage;
